import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Link } from "react-router-dom";
import { User, Heart, MessageSquare, ShoppingBag, Edit, Camera, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", avatar: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setProfile(res.data);
        setForm({ name: res.data.name || "", bio: res.data.bio || "", avatar: res.data.avatar || "" });
      } catch {
        toast.error("Failed to load profile");
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, form);
      setProfile(prev => ({ ...prev, ...res.data }));
      setEditing(false);
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    } finally { setSaving(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, avatar: res.data.url }));
      toast.success("Avatar uploaded");
    } catch { toast.error("Upload failed"); }
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <Navbar />
      <div className="flex-1 flex items-center justify-center"><div className="spinner"></div></div>
    </div>
  );

  const stats = [
    { icon: Heart, label: "Favorites", value: profile?.favorites_count || 0, color: "text-red-400" },
    { icon: MessageSquare, label: "Comments", value: profile?.comments_count || 0, color: "text-purple-400" },
    { icon: ShoppingBag, label: "Orders", value: profile?.orders_count || 0, color: "text-emerald-400" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="My Profile" description="Your FanVerse profile" />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-3xl mx-auto w-full">
        {/* Profile Card */}
        <div className="card-surface p-6 sm:p-8 mb-6" data-testid="profile-card">
          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div className="relative group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-amber-500/30 bg-[var(--bg-elevated)]">
                {(editing ? form.avatar : profile?.avatar) ? (
                  <img src={editing ? form.avatar : profile?.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-10 h-10 text-slate-600" />
                  </div>
                )}
              </div>
              {editing && (
                <label className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-upload" />
                </label>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-3">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Display name"
                    className="input-dark"
                    data-testid="profile-name-input"
                  />
                  <Textarea
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder="Tell us about yourself..."
                    className="input-dark min-h-[80px]"
                    data-testid="profile-bio-input"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={saving} className="btn-primary gap-1" data-testid="profile-save">
                      <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="ghost" onClick={() => { setEditing(false); setForm({ name: profile?.name || "", bio: profile?.bio || "", avatar: profile?.avatar || "" }); }} data-testid="profile-cancel">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl font-bold text-white font-['Outfit']" data-testid="profile-name">{profile?.name || "Fan"}</h1>
                    {profile?.role === "admin" && (
                      <span className="text-[10px] font-mono uppercase bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">Admin</span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm mb-2">{profile?.email}</p>
                  {profile?.bio && <p className="text-slate-300 text-sm">{profile.bio}</p>}
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="mt-2 text-slate-400 hover:text-white gap-1" data-testid="profile-edit-btn">
                    <Edit className="w-3.5 h-3.5" /> Edit Profile
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {stats.map(s => (
            <div key={s.label} className="card-surface p-4 text-center" data-testid={`stat-${s.label.toLowerCase()}`}>
              <s.icon className={`w-5 h-5 ${s.color} mx-auto mb-1`} />
              <div className="text-2xl font-bold text-white font-['Outfit']">{s.value}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Recent Comments */}
          <div className="card-surface p-5">
            <h3 className="text-white font-semibold font-['Outfit'] mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-400" /> Recent Comments
            </h3>
            {(profile?.recent_comments || []).length > 0 ? (
              <div className="space-y-3">
                {profile.recent_comments.map((c, i) => (
                  <div key={i} className="text-sm">
                    <p className="text-slate-300 line-clamp-2">{c.text}</p>
                    <p className="text-slate-600 text-[10px] mt-0.5">{new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600 text-sm">No comments yet</p>
            )}
          </div>

          {/* Recent Orders */}
          <div className="card-surface p-5">
            <h3 className="text-white font-semibold font-['Outfit'] mb-3 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-emerald-400" /> Recent Orders
            </h3>
            {(profile?.recent_orders || []).length > 0 ? (
              <div className="space-y-3">
                {profile.recent_orders.map((o, i) => (
                  <Link key={i} to="/orders" className="flex items-center justify-between text-sm hover:text-white transition-colors">
                    <span className="text-slate-300">Order #{o.id?.slice(-6)}</span>
                    <span className="text-amber-500 font-mono">R{o.total?.toFixed(2)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-slate-600 text-sm">No orders yet</p>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/favorites" className="btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <Heart className="w-3.5 h-3.5" /> My Favorites
          </Link>
          <Link to="/orders" className="btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <ShoppingBag className="w-3.5 h-3.5" /> Order History
          </Link>
          <Link to="/wishlist" className="btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <Heart className="w-3.5 h-3.5" /> Wishlist
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProfilePage;
